/**
 * getJobPayRun — Pay Run Resolver (PR1, pure derivation, no writes)
 * ──────────────────────────────────────────────────────────────────
 * Closes the "every dollar accounted for" gap by deriving a single, sanctioned
 * view of what should be paid (or accrued, for internal labor) on a finished job,
 * and — critically — whether the pay run is *releasable* right now.
 *
 * Companion to `derivePayoutView` in `src/data/portal/contractorPortalSelectors.ts`:
 *   • derivePayoutView   — installer-facing display (pending QA / approved / paid)
 *   • getJobPayRun       — operator/CFO-facing release decision + ledger mapping
 *
 * This PR1 module is INTENTIONALLY:
 *   • A pure function. No `set...`, no localStorage, no React.
 *   • No schema additions. Inputs (callbacks, holdbacks, disputes, labor
 *     estimates/actuals, field-adds) are passed in by the caller from
 *     existing canonical sources.
 *   • Decoupled from `contractValue`. Pay run never reads or returns
 *     customer revenue — only contractor/labor cost.
 *
 * Architectural anchors:
 *   • Hybrid labor costing (Beta Default):
 *       labor_cost_estimated  — from JobSpec phase hours × loaded role rates
 *       labor_cost_actual     — entered by Crew Lead at closeout
 *       labor_cost_variance   — actual − estimated, plus pct
 *       labor_cost_source     — "estimated_jobspec" | "actual_manual" | "payroll_import"
 *     Precedence on display: payroll_import > actual_manual > estimated_jobspec.
 *
 *   • Structured hold reason keys (UI maps to copy elsewhere). The resolver
 *     never emits free-text reasons — copy lives at the consuming surface.
 *
 *   • Tiered field-add approval:
 *       ops_within_cap        — auto-allowed when amount ≤ fieldAddCapAmount
 *       accounting_above_cap  — required when amount > fieldAddCapAmount
 *     The resolver does NOT decide approver identity; it categorizes each
 *     field-add and BLOCKS release when any accounting-tier add is still
 *     `pending` or `rejected`.
 *
 *   • Party → expense_ledger category mapping (mirrors db/migrations/0003):
 *       internal_crew          → "labor"
 *       external_contractor    → "subcontractor_payout"
 *       external_subcontractor → "subcontractor_payout"
 *     Callback-attributable deductions/charges map to "callback_labor"
 *     or "warranty_remediation"; field-adds map to "field_add".
 */

import type { Job, Contractor } from "@/data/types";
import type { ContractorPayProfile } from "@/data/contractorPayProfile";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { HoldbackState } from "@/data/contractor/networkExecution/holdback/deriveState";
import type { DisputeState } from "@/data/contractor/networkExecution/dispute/dispute";
import type { ExpenseLedgerCategory } from "@/data/jobCost/expenseLedgerBridge";

// ── Party kind ─────────────────────────────────────────────────────────────

export type PayRunPartyKind =
  | "internal_crew"
  | "external_contractor"
  | "external_subcontractor";

// ── Labor cost (hybrid model) ──────────────────────────────────────────────

export type LaborCostSource =
  | "estimated_jobspec"
  | "actual_manual"
  | "payroll_import";

export interface LaborCostEstimateInput {
  /** Sum of phase hours × loaded role rates. Required when party is internal. */
  estimatedDollars: number;
  /** Optional breakdown for transparency / future analytics. */
  byRole?: Array<{ role: string; hours: number; loadedRate: number }>;
}

export interface LaborCostActualInput {
  /** Crew-Lead-entered total, computed from actual hours × loaded role rates. */
  actualDollars: number;
  byRole?: Array<{ role: string; hours: number; loadedRate: number }>;
  /** "actual_manual" (default) or "payroll_import". */
  source?: Extract<LaborCostSource, "actual_manual" | "payroll_import">;
  /** Crew Lead user id / name (audit only — not used in math). */
  enteredBy?: string;
  enteredAt?: string;
}

export interface LaborCostView {
  estimated: number | null;
  actual: number | null;
  /** actual − estimated. null when either side is null. */
  variance: number | null;
  /** variance / estimated, as a fraction (0.10 = +10%). null when undefined. */
  variancePct: number | null;
  source: LaborCostSource | null;
}

// ── Field-add approval (tiered) ────────────────────────────────────────────

export type FieldAddStatus = "approved" | "pending" | "rejected";
export type FieldAddTier = "ops_within_cap" | "accounting_above_cap";

export interface FieldAddInput {
  id: string;
  amount: number;
  status: FieldAddStatus;
  /** Optional reason text (passed through, never required by resolver). */
  reason?: string;
  /** Optional approver attribution. Resolver does NOT validate identity. */
  approvedBy?: string;
}

export interface FieldAddRecord extends FieldAddInput {
  tier: FieldAddTier;
}

// ── Callback-attributable deductions ───────────────────────────────────────

export interface CallbackDeduction {
  callbackId: string;
  severity: Callback["severity"];
  amount: number;
  /** Maps the deduction to its ledger category. */
  ledgerCategory: Extract<ExpenseLedgerCategory, "callback_labor" | "warranty_remediation">;
  /** Whether this deduction is currently blocking release (e.g. open warranty). */
  blocksRelease: boolean;
}

// ── Hold reasons (structured keys, no copy here) ───────────────────────────

export type PayRunHoldReason =
  | "failed_qa"
  | "critical_issue_open"
  | "open_dispute"
  | "holdback_required"
  | "warranty_issue_open"
  | "inspection_failed"
  | "field_add_requires_accounting_approval";

// ── Resolver input/output ──────────────────────────────────────────────────

export interface GetJobPayRunInput {
  job: Job;
  /** Pay party. CCC owner with `businessRole === "owner"` → internal_crew. */
  contractor?: Contractor;
  /** Active pay profile for the party (external case). */
  payProfile?: ContractorPayProfile;
  /** End-of-day wrap record, if any. Required to release external pay runs. */
  wrapRecord?: InstallerWrapRecord;
  /** Job-scoped callbacks (from `getCallbacksForExecutor` filtered to job). */
  callbacks?: Callback[];
  /** Job-scoped derived holdback states. */
  holdbacks?: HoldbackState[];
  /** Job-scoped derived dispute states. */
  disputes?: DisputeState[];
  /** Internal-crew labor estimate (from JobSpec phase hours × loaded rates). */
  laborEstimate?: LaborCostEstimateInput;
  /** Internal-crew or external actuals (rare, but allowed for audit). */
  laborActual?: LaborCostActualInput;
  /** Pending/approved field-adds against this job. */
  fieldAdds?: FieldAddInput[];
  /** Open inspection failure flag (caller supplies; derived elsewhere). */
  hasFailedInspection?: boolean;
  /** Open warranty issue flag (caller supplies; derived elsewhere). */
  hasOpenWarrantyIssue?: boolean;
}

export interface PayRunView {
  jobId: string;
  partyKind: PayRunPartyKind;
  partyId?: string;
  partyName?: string;
  /** Base payout amount (external) or estimated/actual labor (internal). */
  baseAmount: number;
  fieldAdds: FieldAddRecord[];
  fieldAddTotal: number;
  /** Sum of just the approved adds (what would actually settle today). */
  fieldAddApprovedTotal: number;
  fieldAddPendingTotal: number;
  fieldAddCap: number;
  requiresAccountingApproval: boolean;
  callbackDeductions: CallbackDeduction[];
  callbackDeductionTotal: number;
  holdbackHeld: number;
  labor: LaborCostView;
  /** Per-party canonical mapping for the BASE row in expense_ledger. */
  ledgerCategory: ExpenseLedgerCategory;
  /** Whether the pay run can be released right now. */
  releasable: boolean;
  /** Structured reason keys — UI provides copy. */
  holdReasons: PayRunHoldReason[];
  /** baseAmount + approved field-adds − callback deductions − holdback. */
  netReleasable: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function resolvePartyKind(
  contractor?: Contractor,
  job?: Job,
): PayRunPartyKind {
  if (contractor?.businessRole === "owner") return "internal_crew";
  // Default external split: full contractor (executor/both) vs. sub.
  if (contractor?.businessRole === "executor" || contractor?.businessRole === "both") {
    return "external_contractor";
  }
  // No contractor record — fall back by job hints.
  if (job?.assignmentType === "Subcontractor") return "external_subcontractor";
  return "external_contractor";
}

function categorizeFieldAdd(
  add: FieldAddInput,
  cap: number,
): FieldAddRecord {
  return {
    ...add,
    tier: add.amount > cap ? "accounting_above_cap" : "ops_within_cap",
  };
}

function resolveLaborView(
  partyKind: PayRunPartyKind,
  estimate?: LaborCostEstimateInput,
  actual?: LaborCostActualInput,
): LaborCostView {
  // Labor view is meaningful for internal crews; external pay runs may
  // still record actuals for analytics but resolver returns nulls when
  // nothing was supplied.
  const est = estimate?.estimatedDollars ?? null;
  const act = actual?.actualDollars ?? null;
  let source: LaborCostSource | null = null;
  if (actual?.source === "payroll_import") source = "payroll_import";
  else if (act != null) source = "actual_manual";
  else if (est != null && partyKind === "internal_crew") source = "estimated_jobspec";
  const variance = est != null && act != null ? act - est : null;
  const variancePct = variance != null && est != null && est > 0 ? variance / est : null;
  return { estimated: est, actual: act, variance, variancePct, source };
}

function resolveLedgerCategory(partyKind: PayRunPartyKind): ExpenseLedgerCategory {
  return partyKind === "internal_crew" ? "labor" : "subcontractor_payout";
}

function sumCallbackDeductions(callbacks: Callback[] = []): CallbackDeduction[] {
  // Conservative default: high-severity warranty callbacks block release;
  // medium/low map to callback_labor without blocking. Callers can extend
  // by passing pre-built deductions in a later PR; for PR1 we infer.
  return callbacks.map((c) => {
    const isWarranty = c.severity === "high";
    return {
      callbackId: c.id,
      severity: c.severity,
      amount: 0, // PR1 does not assign dollar amounts; structure-only.
      ledgerCategory: isWarranty ? "warranty_remediation" : "callback_labor",
      blocksRelease: isWarranty,
    };
  });
}

// ── Resolver ───────────────────────────────────────────────────────────────

export function getJobPayRun(input: GetJobPayRunInput): PayRunView {
  const {
    job,
    contractor,
    payProfile,
    wrapRecord,
    callbacks = [],
    holdbacks = [],
    disputes = [],
    laborEstimate,
    laborActual,
    fieldAdds = [],
    hasFailedInspection = false,
    hasOpenWarrantyIssue = false,
  } = input;

  const partyKind = resolvePartyKind(contractor, job);
  const cap = payProfile?.fieldAddCapAmount ?? 0;

  // Base amount: external → pay profile total or job.payoutAmount;
  //              internal → actual > estimated > 0.
  const labor = resolveLaborView(partyKind, laborEstimate, laborActual);
  let baseAmount = 0;
  if (partyKind === "internal_crew") {
    baseAmount = labor.actual ?? labor.estimated ?? 0;
  } else {
    baseAmount = payProfile?.totalPayoutAmount ?? job.payoutAmount ?? 0;
  }

  const categorizedAdds = fieldAdds.map((a) => categorizeFieldAdd(a, cap));
  const fieldAddTotal = categorizedAdds.reduce((s, a) => s + a.amount, 0);
  const fieldAddApprovedTotal = categorizedAdds
    .filter((a) => a.status === "approved")
    .reduce((s, a) => s + a.amount, 0);
  const fieldAddPendingTotal = categorizedAdds
    .filter((a) => a.status === "pending")
    .reduce((s, a) => s + a.amount, 0);

  const requiresAccountingApproval = categorizedAdds.some(
    (a) => a.tier === "accounting_above_cap" && a.status !== "approved",
  );

  const deductions = sumCallbackDeductions(callbacks);
  const callbackDeductionTotal = deductions.reduce((s, d) => s + d.amount, 0);

  const holdbackHeld = holdbacks
    .filter((h) => h.status === "active" || h.status === "paused")
    .reduce((s, h) => s + h.netHeld, 0);

  // Hold reasons (structured keys only).
  const holdReasons: PayRunHoldReason[] = [];

  // QA gate (external only — internal labor flows differently).
  if (partyKind !== "internal_crew") {
    const qaPassed = wrapRecord?.outcome === "completed_today" && !!job.qaSubmitted;
    if (!qaPassed) holdReasons.push("failed_qa");
  }

  if (deductions.some((d) => d.blocksRelease)) holdReasons.push("warranty_issue_open");
  if (hasOpenWarrantyIssue && !holdReasons.includes("warranty_issue_open")) {
    holdReasons.push("warranty_issue_open");
  }
  if (hasFailedInspection) holdReasons.push("inspection_failed");
  if (disputes.some((d) => d.status === "open")) holdReasons.push("open_dispute");
  if (holdbackHeld > 0) holdReasons.push("holdback_required");
  if (requiresAccountingApproval) holdReasons.push("field_add_requires_accounting_approval");

  // Critical (high-severity) callback open → block, distinct from warranty.
  if (callbacks.some((c) => c.severity === "high")) {
    if (!holdReasons.includes("critical_issue_open")) holdReasons.push("critical_issue_open");
  }

  const releasable = holdReasons.length === 0 && baseAmount > 0;
  const netReleasable = Math.max(
    0,
    baseAmount + fieldAddApprovedTotal - callbackDeductionTotal - holdbackHeld,
  );

  return {
    jobId: job.id,
    partyKind,
    partyId: contractor?.id,
    partyName: contractor?.companyName,
    baseAmount,
    fieldAdds: categorizedAdds,
    fieldAddTotal,
    fieldAddApprovedTotal,
    fieldAddPendingTotal,
    fieldAddCap: cap,
    requiresAccountingApproval,
    callbackDeductions: deductions,
    callbackDeductionTotal,
    holdbackHeld,
    labor,
    ledgerCategory: resolveLedgerCategory(partyKind),
    releasable,
    holdReasons,
    netReleasable,
  };
}

/**
 * Convenience predicate. Mirrors the hard rule the future `recordPayRun()`
 * writer in PR2 must enforce: NEVER release while `requiresAccountingApproval`
 * is true with `fieldAddTotal > fieldAddCap`.
 */
export function isPayRunReleasable(view: PayRunView): boolean {
  return view.releasable;
}
